import React, { useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Formik, FormikProps, isNaN } from 'formik';
import * as Yup from 'yup';
import { DCF_YEARS, FairPriceMethodEnum } from '@app/common';
import { BaseDialog } from '../base-dialog';
import { FairPriceDialogContent } from './fair-price-dialog-content';
import { Button } from '../../form/button/button';
import { useRootStore } from '../../../../providers/root-store-provider';
import { FairPriceFormValues } from '../../fair-price/form-values/fair-price-form-values-interface';
import { Confirm } from '../../confirm/confirm';

const parseNum = (val: string) => Number((val ?? '').replace(/\s/g, '').replace(',', '.'));
const isValidNum = (val?: string) => !val || !isNaN(parseNum(val));

const createFairPriceSchema = (msg: string) => {
    const numField = () => Yup.string().test('num', msg, isValidNum);
    return Yup.object({
        peRequiredReturn: numField(),
        fcfRequiredReturn: numField(),
        eps: numField(),
        peMultiple: numField(),
        fcfPerShare: numField(),
        fcfGrowthRate: numField(),
        fcfTargetYield: numField(),
        dcfTotalFcf: numField(),
        dcfGrowthRate: numField(),
        dcfTerminalGrowthRate: numField(),
        dcfDiscountRate: numField(),
        dcfMarginOfSafety: numField(),
        dcfCash: numField(),
        dcfDebt: numField(),
        dcfSharesOutstanding: numField(),
        growthRate: Yup.number().nullable().typeError(msg),
    });
};

const activeTabFields: Record<FairPriceMethodEnum, Array<keyof FairPriceFormValues>> = {
    [FairPriceMethodEnum.PE]: ['eps', 'growthRate', 'peMultiple', 'peRequiredReturn'],
    [FairPriceMethodEnum.FCF]: ['fcfPerShare', 'fcfGrowthRate', 'fcfTargetYield', 'fcfRequiredReturn'],
    [FairPriceMethodEnum.DCF]: [
        'dcfTotalFcf',
        'dcfGrowthRate',
        'dcfTerminalGrowthRate',
        'dcfDiscountRate',
        'dcfMarginOfSafety',
        'dcfCash',
        'dcfDebt',
        'dcfSharesOutstanding',
    ],
};

const normalizeValue = (value: FairPriceFormValues[keyof FairPriceFormValues]) => String(value ?? '');

const isActiveTabDefault = (
    values: FairPriceFormValues,
    defaultValues: FairPriceFormValues,
    activeTab: FairPriceMethodEnum,
    dcfYears: 5 | 10,
) => {
    if (activeTab === FairPriceMethodEnum.DCF && dcfYears !== DCF_YEARS) return false;

    return activeTabFields[activeTab].every(
        (field) => normalizeValue(values[field]) === normalizeValue(defaultValues[field]),
    );
};

export const FairPriceDialog: React.FC = observer(() => {
    const { t } = useTranslation();
    const fairPriceSchema = useMemo(
        () => createFairPriceSchema(t('layout.dialogs.fairPrice.validation.mustBeNumber')),
        [t],
    );
    const {
        symbolDetailFairPriceDialogStore,
        symbolsStore: { symbolFairPriceStore },
    } = useRootStore();

    const initialValuesRef = useRef(symbolFairPriceStore.formValues);
    const formikRef = useRef<FormikProps<FairPriceFormValues> | null>(null);

    const { activeTab, loading } = symbolFairPriceStore;
    const dialogOpened = symbolDetailFairPriceDialogStore.fairPriceDialogOpened;

    useEffect(() => {
        if (!loading && dialogOpened && formikRef.current) {
            const values = symbolFairPriceStore.formValues;
            formikRef.current.resetForm({ values });
        }
    }, [loading, dialogOpened]);

    const onSubmit = async (values: FairPriceFormValues) => {
        symbolFairPriceStore.applyFormValues(values);
        const success = await symbolFairPriceStore.saveFairPriceSettings();
        if (success) {
            symbolDetailFairPriceDialogStore.closeFairPriceDialog();
        }
    };

    const handleSave = () => {
        formikRef.current?.submitForm();
    };

    return (
        <Formik<FairPriceFormValues>
            initialValues={initialValuesRef.current}
            onSubmit={onSubmit}
            innerRef={formikRef}
            validationSchema={fairPriceSchema}
            validateOnChange
        >
            {(formikProps) => {
                const method = {
                    [FairPriceMethodEnum.PE]: 'P/E',
                    [FairPriceMethodEnum.FCF]: 'FCF',
                    [FairPriceMethodEnum.DCF]: 'DCF',
                }[activeTab];

                const allFieldsFilled = activeTabFields[activeTab].every(
                    (field) => normalizeValue(formikProps.values[field]) !== '',
                );

                const saveDisabled = loading || formikProps.isSubmitting || !formikProps.isValid || !allFieldsFilled;

                const resetDisabled =
                    loading ||
                    isActiveTabDefault(
                        formikProps.values,
                        symbolFairPriceStore.defaultFormValues,
                        activeTab,
                        symbolFairPriceStore.dcfYears,
                    );

                const handleReset = async () => {
                    const success = await symbolFairPriceStore.resetActiveTabAndSave();
                    if (!success) return;

                    const values = symbolFairPriceStore.formValues;
                    formikProps.resetForm({ values });
                };

                return (
                    <BaseDialog
                        opened={symbolDetailFairPriceDialogStore.fairPriceDialogOpened}
                        close={() => symbolDetailFairPriceDialogStore.closeFairPriceDialog()}
                        title={t(
                            activeTab === FairPriceMethodEnum.DCF
                                ? 'layout.dialogs.fairPrice.titleDcf'
                                : 'layout.dialogs.fairPrice.title',
                        )}
                        content={<FairPriceDialogContent formikProps={formikProps} />}
                        action={
                            <>
                                <Confirm
                                    isProcessing={loading}
                                    onConfirm={handleReset}
                                    title={t('layout.dialogs.fairPrice.resetConfirm.title', { method })}
                                    content={t('layout.dialogs.fairPrice.resetConfirm.content')}
                                    cancelTitle={t('layout.confirm.cancel')}
                                    confirmTitle={t('layout.dialogs.fairPrice.resetConfirm.confirm')}
                                    position={'top'}
                                >
                                    <Button variant={'negative'} variation={'outlined'} disabled={resetDisabled}>
                                        {t('layout.dialogs.fairPrice.reset', { method })}
                                    </Button>
                                </Confirm>
                                <Button variant={'primary'} onClick={handleSave} disabled={saveDisabled}>
                                    {t('layout.dialogs.fairPrice.save')}
                                </Button>
                            </>
                        }
                        width={94}
                    />
                );
            }}
        </Formik>
    );
});
